import { unarchiveJob } from '../../utils/unarchiveJob';
import { isEmpty, map, omit } from 'lodash-es';
import React, { useState, useEffect } from 'react';
import { EmptyCard } from 'components/emptyCard/emptyCard';
import { JobIndexCard } from 'components/jobIndexCard/JobIndexCard';
import styles from './JobIndexList.module.scss';
import classNames from 'classnames';
import { Button } from 'components/button/button';
import { useNavigate } from 'react-router-dom';
import { routesPath } from '../../constants/routesPath';
import { baseUrl } from '@constants/baseUrl'; 
import toast from 'react-hot-toast'; 
import { authHeader } from '../../constants/authenticationHeader';

export const JobIndexList = (props) => {
  const {
    jobs,
    colors,
    jobStatuses,
    deleteJob,
    update,
    create,
    activeJobStatus,
    archivedJobStatus,
    deletedJobStatus,
    jobsFilter,
    showArchived,
    showDrafts,
    onClientFilter,
  } = props;

  const navigate = useNavigate();
  
  // State to force re-render when archived jobs are updated
  const [archivedJobsUpdateTrigger, setArchivedJobsUpdateTrigger] = useState(0);
  const [recentlyArchivedJobs, setRecentlyArchivedJobs] = useState([]);
  const [recentlyRestoredJobs, setRecentlyRestoredJobs] = useState([]);
  const [recentlyDraftedJobs, setRecentlyDraftedJobs] = useState([]);
  
  // Load archived/restored/drafted jobs from localStorage
  useEffect(() => {
    let archivedJobs = [];
    let restoredJobs = [];
    let draftedJobs = [];
    let deletedJobIds = [];
    
    try {
      // Load deleted job IDs to filter them out
      const deletedIdsStr = localStorage.getItem('deletedJobIds');
      if (deletedIdsStr) {
        deletedJobIds = JSON.parse(deletedIdsStr);
      }
      
      const storedArchived = localStorage.getItem('recentlyArchivedJobs');
      if (storedArchived) {
        archivedJobs = JSON.parse(storedArchived);
        window.recentlyArchivedJobs = archivedJobs;
      }
      
      const storedRestored = localStorage.getItem('recentlyRestoredJobs');
      if (storedRestored) {
        const allRestoredJobs = JSON.parse(storedRestored);
        const now = new Date();
        
        // Filter out restored jobs older than 1 hour to prevent stale entries
        restoredJobs = allRestoredJobs.filter(job => {
          const restoredTime = new Date(job.restoredAt);
          const hoursDiff = (now - restoredTime) / (1000 * 60 * 60);
          return hoursDiff <= 1; // Keep only jobs restored within last hour
        });
        
        // Update localStorage if we filtered out old jobs
        if (restoredJobs.length !== allRestoredJobs.length) {
          localStorage.setItem('recentlyRestoredJobs', JSON.stringify(restoredJobs));
        }
      }
      
      const storedDrafted = localStorage.getItem('recentlyDraftedJobs');
      if (storedDrafted) {
        const allDraftedJobs = JSON.parse(storedDrafted);
        
        // Filter out any deleted jobs from the drafted list
        draftedJobs = allDraftedJobs.filter(job => 
          !deletedJobIds.includes(String(job.id)) && !deletedJobIds.includes(job.id)
        );
        
        // Update localStorage if we filtered out deleted jobs
        if (draftedJobs.length !== allDraftedJobs.length) {
          localStorage.setItem('recentlyDraftedJobs', JSON.stringify(draftedJobs));
        }
        
        window.recentlyDraftedJobs = draftedJobs;
      }
    } catch (e) {
    }
    
    setRecentlyArchivedJobs(archivedJobs);
    setRecentlyRestoredJobs(restoredJobs);
    setRecentlyDraftedJobs(draftedJobs);
  }, [archivedJobsUpdateTrigger]); // Re-run when trigger changes
  
  // Listen for archived jobs updates
  useEffect(() => {
    const handleArchivedJobsUpdate = () => {
      setArchivedJobsUpdateTrigger(prev => prev + 1);
    };
    
    window.addEventListener('archivedJobsUpdated', handleArchivedJobsUpdate);
    
    return () => {
      window.removeEventListener('archivedJobsUpdated', handleArchivedJobsUpdate);
    };
  }, []);
  
  // Listen for drafted jobs updates
  useEffect(() => {
    const handleDraftedJobsUpdate = () => {
      try {
        const storedDrafted = localStorage.getItem('recentlyDraftedJobs');
        if (storedDrafted) {
          const draftedJobs = JSON.parse(storedDrafted);
          setRecentlyDraftedJobs(draftedJobs);
          window.recentlyDraftedJobs = draftedJobs;
        }
      } catch (e) {
        console.error('Error updating drafted jobs:', e);
      }
    };
    
    window.addEventListener('draftedJobsUpdated', handleDraftedJobsUpdate);
    
    return () => {
      window.removeEventListener('draftedJobsUpdated', handleDraftedJobsUpdate);
    };
  }, []);
  
  // Process and enhance job data
  let enhancedJobs = [];
  
  if (!isEmpty(jobs)) {
    enhancedJobs = jobs.map(job => {
      // Ensure job has required properties by creating a fresh copy
      const enhancedJob = {...job};
      
      // Check if job should be considered archived
      const isArchived = 
        job.archived === 1 || 
        job.archived === "1" || 
        job.archived === true ||
        job.status === "20" || 
        job.status === 20 ||
        job.expired === 1 || 
        job.expired === "1" ||
        job.status === "archived" ||
        job.job?.archived === 1 || 
        job.job?.archived === "1" || 
        job.job?.archived === true ||
        job.job?.status === "archived" ||
        job.job?.status === "20" ||
        job.job?.expired === 1;
      
      // If job should be considered archived, set all archived flags
      if (isArchived) {
        enhancedJob.archived = 1;
        enhancedJob.hidden = 0; // Ensure not hidden
        enhancedJob.status = enhancedJob.status || "20"; // Set status if not already set
      }
      
      return enhancedJob;
    });
  }
  
  // Process jobs for display
  let finalJobs = [];
  
  if (showArchived) {
    // In archived view, we need to build the list from multiple sources
    // 1. First, add jobs from the API that are marked as archived
    if (jobs && Array.isArray(jobs)) {
      jobs.forEach(job => {
        const isArchived = 
          job.archived === 1 || 
          job.archived === "1" || 
          job.archived === true ||
          job.status === "20" || 
          job.status === 20 ||
          job.expired === 1 || 
          job.expired === "1" ||
          job.status === "archived" ||
          job.job?.archived === 1 || 
          job.job?.archived === "1" || 
          job.job?.archived === true ||
          job.job?.status === "archived" ||
          job.job?.status === "20" ||
          job.job?.expired === 1;
        
        if (isArchived) {
          finalJobs.push({...job, archived: 1, hidden: 0});
        }
      });
    }
    
    // 2. Add recently archived jobs from localStorage
    if (recentlyArchivedJobs.length > 0) {
      recentlyArchivedJobs.forEach(archivedJobInfo => {
        const foundJob = jobs?.find(job => String(job.id) === String(archivedJobInfo.id));
        if (foundJob) {
          // Job exists in API response, make sure it's in final jobs with archived status
          if (!finalJobs.some(job => String(job.id) === String(foundJob.id))) {
            finalJobs.push({
              ...foundJob, 
              archived: 1, 
              hidden: 0,
              status: "20",
              expired: 1
            });
          }
        } else {
          // Job missing from API response (likely filtered out), create a display entry from localStorage
          
          // Create a basic job entry for display purposes
          const archivedJobDisplay = {
            id: archivedJobInfo.id,
            title: archivedJobInfo.title || `Archived Job ${archivedJobInfo.id}`,
            company_name: archivedJobInfo.company_name || 'Archived Company',
            company_logo: archivedJobInfo.company_logo || null,
            hidden: 0,
            archived: 1,
            status: "20",
            expired: 1,
            views: archivedJobInfo.views || 0,
            created: archivedJobInfo.created || archivedJobInfo.archivedAt?.split('T')[0] || new Date().toISOString().split('T')[0],
            expiry_date: archivedJobInfo.expiry_date || "2025-12-31",
            job_applications: archivedJobInfo.job_applications || [],
            url: archivedJobInfo.url || `/rest/jobs/${archivedJobInfo.id}`, // Construct URL for API calls
            // Add any other fields that were stored in localStorage
            ...archivedJobInfo
          };
          
          finalJobs.push(archivedJobDisplay);
        }
      });
    }
  } else if (showDrafts) {
    // In draft view, show only jobs that are in the recentlyDraftedJobs list
    // OR have draft status flags from API
    finalJobs = enhancedJobs.filter(job => {
      // Check if this job ID is in the drafted jobs list
      const isInDraftList = recentlyDraftedJobs.some(draftedJob => 
        String(draftedJob.id) === String(job.id)
      );
      
      // A job is considered draft if it has hidden === 1 AND job_status === 0
      const isDraftFromAPI = 
        (job.hidden === 1 || job.hidden === "1") &&
        (job.job_status === 0 || job.job_status === "0");
      
      const isNotArchived = !(
        job.archived === 1 || 
        job.archived === "1" || 
        job.status === "20" || 
        job.status === 20 ||
        job.expired === 1 || 
        job.expired === "1"
      );
      
      // Show if it's in draft list OR has draft flags from API, and not archived
      const shouldShow = (isInDraftList || isDraftFromAPI) && isNotArchived;
      
      if (shouldShow && isInDraftList) {
        console.log('✅ Draft job found in localStorage:', { id: job.id, title: job.title, isInDraftList });
      }
      
      return shouldShow;

    });
    
    // Add recently drafted jobs from localStorage
    if (recentlyDraftedJobs.length > 0) {
      recentlyDraftedJobs.forEach(draftedJobInfo => {
        // Check if job is already in finalJobs to prevent duplicates
        const jobAlreadyInList = finalJobs.some(job => String(job.id) === String(draftedJobInfo.id));
        
        if (!jobAlreadyInList) {
          const foundJob = jobs?.find(job => String(job.id) === String(draftedJobInfo.id));
          if (foundJob) {
            // Job exists in API response, make sure it's in final jobs with draft status
            finalJobs.push({
              ...foundJob, 
              hidden: 1,
              job_status: 0,
              status: 0,
              archived: 0
            });
          } else {
            // Job missing from API response (likely filtered out), create a display entry from localStorage
            
            // Create a basic job entry for display purposes
            const draftedJobDisplay = {
              id: draftedJobInfo.id,
              title: draftedJobInfo.title || `Draft Job ${draftedJobInfo.id}`,
              company_name: draftedJobInfo.company_name || 'Draft Company',
              company_logo: draftedJobInfo.company_logo || null,
              hidden: 1,
              job_status: 0,
              status: 0,
              archived: 0,
              views: draftedJobInfo.views || 0,
              created: draftedJobInfo.created || draftedJobInfo.draftedAt?.split('T')[0] || new Date().toISOString().split('T')[0],
              expiry_date: draftedJobInfo.expiry_date || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              job_applications: draftedJobInfo.job_applications || [],
              url: draftedJobInfo.url || `/rest/jobs/${draftedJobInfo.id}`, // Construct URL for API calls
              // Add any other fields that were stored in localStorage
              ...draftedJobInfo
            };
            
            finalJobs.push(draftedJobDisplay);
          }
        }
      });
    }
  } else {
    // In active view, show only active jobs (not archived, not draft)
    finalJobs = enhancedJobs.filter(job => {
      // Exclude archived jobs
      const isArchived = 
        job.archived === 1 || 
        job.archived === "1" || 
        job.status === "20" || 
        job.status === 20 ||
        job.expired === 1 || 
        job.expired === "1";
      
      // Exclude draft jobs - a job is draft if BOTH hidden === 1 AND job_status === 0
      // OR if it's in the recently drafted list
      const isDraft = 
        (job.hidden === 1 || job.hidden === "1") &&
        (job.job_status === 0 || job.job_status === "0");
        
      const isRecentlyArchived = recentlyArchivedJobs.some(archivedJob => 
        String(archivedJob.id) === String(job.id)
      );
      
      // Check if this job ID is in the drafted jobs list
      // This will exclude the new draft copies from active view
      const isRecentlyDrafted = recentlyDraftedJobs.some(draftedJob => 
        String(draftedJob.id) === String(job.id)
      );
      
      // Show job only if it's not archived, not draft, and not recently archived/drafted
      const shouldShow = !isArchived && !isDraft && !isRecentlyArchived && !isRecentlyDrafted;
      
      if (!shouldShow && isRecentlyDrafted) {
        console.log('🚫 Draft job filtered out from active view:', { id: job.id, title: job.title, isRecentlyDrafted });
      }
      
      return shouldShow;
    });
  }

  // Regular rendering path
  return (
    <div className={classNames(styles.listWrapper)}>
      {!isEmpty(finalJobs) && (
        <div className={styles.jobsTotal}>
          {finalJobs.length} job{finalJobs.length !== 1 ? 's' : ''} {showArchived ? 'archived' : 'active'}
        </div>
      )}
      {!isEmpty(finalJobs) ? (
        map(finalJobs, (job, i) => {
          const newJob = omit({ ...job?.job }, [
            'id',
            'created',
            'published',
            'modified',
            'hl5_url',
            'last_synced',
            'num_views',
          ]);

          return (
                          <JobIndexCard
                job={job}
                colors={colors}
                client={job?.company_name}
                clientLogo={job?.company_logo}
                key={job?.id}
                jobStatuses={jobStatuses}
                showArchived={showArchived}
                showDrafts={showDrafts}
                onClientClick={onClientFilter}
                clickHandlers={{
                activatedHandler: () => {
                  if (showDrafts) {
                    // Publish draft job
                    toast.loading(`Publishing draft job "${job.title || 'Untitled Job'}"...`);
                    
                    // Call API to update job status to active
                    fetch(`${baseUrl}/rest/jobs/update`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...authHeader().common,
                      },
                      body: JSON.stringify({
                        id: job.id,
                        job_status: 1, // Active status
                        status: 1, // Active
                        hidden: 0, // Make visible (not hidden anymore)
                        archived: 0,
                        expired: 0,
                      }),
                    })
                      .then(response => response.json())
                      .then(result => {
                        if (result.success) {
                          // Remove from drafted jobs in localStorage
                          try {
                            const storedDrafted = JSON.parse(localStorage.getItem('recentlyDraftedJobs') || '[]');
                            const updatedDrafted = storedDrafted.filter(draftedJob => 
                              String(draftedJob.id) !== String(job.id)
                            );
                            localStorage.setItem('recentlyDraftedJobs', JSON.stringify(updatedDrafted));
                            
                            if (window.recentlyDraftedJobs) {
                              window.recentlyDraftedJobs = window.recentlyDraftedJobs.filter(draftedJob => 
                                String(draftedJob.id) !== String(job.id)
                              );
                            }
                          } catch (e) {
                            console.error('Failed to remove job from drafted list:', e);
                          }
                          
                          toast.dismiss();
                          toast.success('Draft job published successfully!');
                          
                          // Switch to active view
                          setTimeout(() => {
                            const event = new CustomEvent('switchToActiveView', { detail: { jobId: job.id } });
                            window.dispatchEvent(event);
                            
                            setTimeout(() => {
                              window.location.reload();
                            }, 200);
                          }, 500);
                        } else {
                          throw new Error(result?.message || 'Failed to publish draft');
                        }
                      })
                      .catch((error) => {
                        toast.dismiss();
                        toast.error('Failed to publish draft job. Please try again.');
                        console.error('Publish draft error:', error);
                      });
                  } else if (showArchived) {
                    // Unarchive job
                    // Provide feedback to user
                    toast.loading(`Restoring job "${job.title || 'Untitled Job'}" to active status...`);
                    
                    // Try using the update function first if available
                    if (typeof update === 'function' && activeJobStatus && job?.url) {
                      try {
                        const result = update(job.url, { job_status: activeJobStatus });
                        
                        // Clean up localStorage
                        try {
                          const storedArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
                          const updatedArchived = storedArchived.filter(archivedJob => 
                            String(archivedJob.id) !== String(job.id)
                          );
                          localStorage.setItem('recentlyArchivedJobs', JSON.stringify(updatedArchived));
                          
                          // ADD TO RECENTLY RESTORED JOBS for active view
                          const restoredJobInfo = {
                            id: job.id,
                            title: job.title,
                            restoredAt: new Date().toISOString()
                          };
                          
                          try {
                            const existingRestored = JSON.parse(localStorage.getItem('recentlyRestoredJobs') || '[]');
                            // Remove any previous entries for this job to avoid duplicates
                            const filteredRestored = existingRestored.filter(restoredJob => 
                              String(restoredJob.id) !== String(job.id)
                            );
                            filteredRestored.push(restoredJobInfo);
                            const recentRestored = filteredRestored.slice(-10);
                            localStorage.setItem('recentlyRestoredJobs', JSON.stringify(recentRestored));
                          } catch (e) {
                            console.error('Failed to store restored job in localStorage:', e);
                          }
                          
                          if (window.recentlyArchivedJobs) {
                            window.recentlyArchivedJobs = window.recentlyArchivedJobs.filter(archivedJob => 
                              String(archivedJob.id) !== String(job.id)
                            );
                          }
                        } catch (e) {
                          console.error('Failed to remove job from recently archived list:', e);
                        }
                        
                        toast.success('Job restored successfully!');
                        
                        // Switch to active view
                        const event = new CustomEvent('switchToActiveView', { detail: { jobId: job.id } });
                        window.dispatchEvent(event);
                        
                        setTimeout(() => {
                          window.location.reload();
                        }, 500);
                        
                        return; // Exit early since we used the update function
                      } catch (updateError) {
                        // Update function failed, fall back to API call
                      }
                    }
                    
                    // Use the utility function for unarchiving
                    unarchiveJob(job.id)
                      .then(result => {
                        if (result.success) {
                          try {
                            const storedArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
                            const updatedArchived = storedArchived.filter(archivedJob => 
                              String(archivedJob.id) !== String(job.id)
                            );
                            localStorage.setItem('recentlyArchivedJobs', JSON.stringify(updatedArchived));
                            
                            // ADD TO RECENTLY RESTORED JOBS for active view
                            const restoredJobInfo = {
                              id: job.id,
                              title: job.title,
                              restoredAt: new Date().toISOString()
                            };
                            
                            try {
                              const existingRestored = JSON.parse(localStorage.getItem('recentlyRestoredJobs') || '[]');
                              // Remove any previous entries for this job to avoid duplicates
                              const filteredRestored = existingRestored.filter(restoredJob => 
                                String(restoredJob.id) !== String(job.id)
                              );
                              filteredRestored.push(restoredJobInfo);
                              const recentRestored = filteredRestored.slice(-10);
                              localStorage.setItem('recentlyRestoredJobs', JSON.stringify(recentRestored));
                            } catch (e) {
                              console.error('Failed to store restored job in localStorage:', e);
                            }
                            
                            // Also update window object
                            if (window.recentlyArchivedJobs) {
                              window.recentlyArchivedJobs = window.recentlyArchivedJobs.filter(archivedJob => 
                                String(archivedJob.id) !== String(job.id)
                              );
                            }
                          } catch (e) {
                            console.error('Failed to remove job from recently archived list:', e);
                          }
                          
                          toast.success('Job restored successfully!');
                          
                          // Switch to active view to show the restored job
                          const event = new CustomEvent('switchToActiveView', { detail: { jobId: job.id } });
                          window.dispatchEvent(event);
                          
                          // Force page reload after a short delay to get fresh data
                          setTimeout(() => {
                            window.location.reload();
                          }, 500);
                        } else {
                          throw new Error(result.message || 'Unknown error occurred');
                        }
                      })
                      .catch((err) => {
                        // Show error message to user
                        const errorMsg = `Error restoring job: ${err.message}`;
                        toast.error(errorMsg);
                        
                        // Still reload to get fresh data from server
                        setTimeout(() => {
                          window.location.reload();
                        }, 1000);
                      });
                  } else {
                    typeof update === 'function'
                      ? update(job?.url, { job_status: activeJobStatus })
                      : toast.error('Update function not available');
                  }
                },
                archivedHandler: () => {
                  if (typeof update === 'function') {
                    update(job?.url, { job_status: archivedJobStatus });
                  } else {
                    // Use confirmation before proceeding
                    if (!window.confirm(`Are you sure you want to archive job "${job.title || 'Untitled Job'}"?`)) {
                      return;
                    }
                    
                    // Try API call with better error handling
                    fetch(`${baseUrl}/rest/jobs/archive`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...authHeader().common,
                      },
                      body: JSON.stringify({ id: job.id }),
                    })
                      .then(res => {
                        // Directly modify the job for immediate feedback
                        job.archived = 1;
                        
                        // Better error handling
                        if (!res.ok) {
                          throw new Error(`Server returned ${res.status}: ${res.statusText}`);
                        }
                        
                        return res.json().catch(() => {
                          // If not JSON, just return the status
                          return { status: 'success', message: 'Job archived successfully' };
                        });
                      })
                      .then(data => {
                        toast.success('Job archived successfully!');
                                                
                        // Store the archived job ID for debugging - USE LOCALSTORAGE for persistence
                        if (!window.recentlyArchivedJobs) {
                          window.recentlyArchivedJobs = [];
                        }
                        const archivedJobInfo = {
                          id: job.id,
                          title: job.title,
                          company_name: job.company_name,
                          company_logo: job.company_logo,
                          logo: job.logo || job.company_logo,
                          acronym: job.acronym,
                          indicator_acronym: job.indicator_acronym,
                          views: job.views,
                          job_applications: job.job_applications,
                          created: job.created,
                          expiry_date: job.expiry_date,
                          url: job.url,
                          company: job.company, // Include company object if it exists
                          archivedAt: new Date().toISOString()
                        };
                        
                        window.recentlyArchivedJobs.push(archivedJobInfo);
                        
                        try {
                          const existingArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
                          existingArchived.push(archivedJobInfo);
                          // Keep only the last 10 archived jobs to prevent localStorage bloat
                          const recentArchived = existingArchived.slice(-10);
                          localStorage.setItem('recentlyArchivedJobs', JSON.stringify(recentArchived));
                          
                          // Remove from recently restored jobs if it exists there
                          try {
                            const existingRestored = JSON.parse(localStorage.getItem('recentlyRestoredJobs') || '[]');
                            const updatedRestored = existingRestored.filter(restoredJob => 
                              String(restoredJob.id) !== String(job.id)
                            );
                            localStorage.setItem('recentlyRestoredJobs', JSON.stringify(updatedRestored));
                          } catch (e) {
                            console.error('Failed to remove job from recently restored list:', e);
                          }
                        } catch (e) {
                          console.error('Failed to store archived job in localStorage:', e);
                        }
                        
                        const currentUrl = window.location.href;
                        if (!currentUrl.includes('archived')) {
                          // Force reload with archived view after a short delay to allow API to update
                          setTimeout(() => {
                            // Try to trigger the archived view programmatically first
                            const event = new CustomEvent('switchToArchivedView', { detail: { jobId: job.id } });
                            window.dispatchEvent(event);
                            
                            // Then reload as backup
                            setTimeout(() => {
                              window.location.reload();
                            }, 200);
                          }, 800); // Give API more time to update
                        } else {
                          // We're already on archived view, just reload
                          setTimeout(() => {
                            window.location.reload();
                          }, 800);
                        }
                      })
                      .catch((err) => {
                        toast.error('There was an error archiving the job. Reloading page to refresh data...');
                        
                        // Reload anyway to get fresh data from server
                        setTimeout(() => {
                          window.location.reload();
                        }, 500);
                      });
                  }
                },
                editHandler: () => {
                  // Navigate to job edit form
                  navigate(routesPath.jobEdit(job.id), { replace: false });
                },
                deletedHandler: () => {
                  const isDraft = (job.hidden === 1 || job.hidden === "1") && 
                                 (job.job_status === 0 || job.job_status === "0");
                  return deleteJob(job?.id, job?.title, isDraft);
                },
                duplicateHandler: () => {
                  // Store the full job data in localStorage for duplication
                  const jobDataForDuplication = {
                    ...job,
                    title: `${job.title || 'Untitled Job'} (Copy)`,
                    description: job.description || job.job?.description || '',
                    client_id: job.client_id || job.client?.id || null,
                    isDuplicate: true,
                  };
                  
                  localStorage.setItem('duplicateJobData', JSON.stringify(jobDataForDuplication));
                  
                  // Navigate to job form page - the form will load the data and user can save as draft
                  toast.success('Opening job for duplication...');
                  navigate(routesPath.jobsFormPage, { replace: false });
                },
              }}
              onClick={(e) => {
                // For draft jobs, navigate to edit form instead of applicants page
                if (showDrafts) {
                  navigate(routesPath.jobsFormPage + `?id=${job.id}`, { replace: false });
                } else {
                  navigate(routesPath.jobApplicants(job.id), { replace: false });
                }
              }}
              jobsFilter={jobsFilter}
            />
          );
        })
      ) : isEmpty(finalJobs) ? (
        <div className={styles.card_container}>
          <center>
            <EmptyCard
              isCard
              title={showDrafts 
                ? "No Draft Jobs" 
                : showArchived 
                  ? "No Archived Jobs" 
                  : "You don't have any job posts to display here. Create one now."}
              description={showDrafts
                ? "When you duplicate a job or save it as a draft, it will appear here. You can edit and publish drafts at any time."
                : showArchived
                  ? "When you archive jobs, they will appear here. You can restore archived jobs to active status at any time."
                  : "It's quick and easy to create and publish jobs on Holedo. In no time your job post will be live for thousands of potential candidates."
              }
            >
              {!showArchived && (
                <Button
                  submit
                  inline
                  onClick={() => navigate(routesPath.jobsFormPage, { replace: false })}
                >
                  New job post
                </Button>
              )}
            </EmptyCard>
          </center>
        </div>
      ) : (
        <div className={classNames(styles.alert, styles.green, styles.big)}>
          <center>There has been an error</center>
        </div>
      )}
    </div>
  );
};
